/**
 * Custom Mobile Menu Toggle
 *
 * 1. Publishes the header's real bottom edge as `--mobile-drawer-top` so the
 *    drawer can start below the header instead of at a hardcoded offset.
 * 2. Swaps the hamburger icon to an X while the drawer is open.
 *
 * Both previously keyed off `document.body.classList.contains('scroll-lock')`
 * to decide whether the menu was open. That is a GLOBAL flag — the cart drawer,
 * cart notification and newsletter popup all set it. If any of them left it set
 * (or set it while the menu was closed), this script's capture-phase handler
 * would call preventDefault() + stopImmediatePropagation() and try to CLOSE a
 * menu that was never open, so the hamburger became permanently dead. That is
 * the "menu doesn't open at all on my phone" report. Open state is now read
 * from the dialog itself.
 */

/* ------------------------------------------------------------------ *
 * 1. Drawer offset — must run whether or not the trigger exists.
 * ------------------------------------------------------------------ */
(function () {
  function publishDrawerTop() {
    var header = document.querySelector('.\\#header');
    if (!header) return;
    // Bottom edge in viewport coords. The header and announcement bar are both
    // position:fixed on mobile, so this already accounts for the announcement
    // bar — including when the shopper dismisses it and the header moves up.
    var bottom = Math.max(0, Math.round(header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--mobile-drawer-top', bottom + 'px');
  }

  function schedule() {
    requestAnimationFrame(publishDrawerTop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
  window.addEventListener('load', schedule);
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);

  // The announcement bar can be closed, which changes the header's bottom edge.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[class*="announcement-bar"]')) schedule();
  }, true);

  // Catch late layout shifts (web fonts, announcement slideshow, app embeds).
  if (typeof ResizeObserver === 'function') {
    var header = document.querySelector('.\\#header');
    if (header) new ResizeObserver(schedule).observe(header);
  }
})();

/* ------------------------------------------------------------------ *
 * 2. Hamburger <-> X icon swap.
 * ------------------------------------------------------------------ */
document.addEventListener('DOMContentLoaded', function () {
  var menuTrigger = document.querySelector('.\\#main-navigation-mobile-trigger');
  if (!menuTrigger) return;

  var iconContainer = menuTrigger.querySelector('.\\#main-navigation-mobile-icon');
  if (!iconContainer) return;

  menuTrigger.style.position = 'relative';
  menuTrigger.style.zIndex = '10002';

  var hamburgerIcon = '<svg class="#icon"><use href="#svg-icon-bars"></use></svg>';
  var closeIcon = '<svg class="#icon"><use href="#svg-icon-x"></use></svg>';

  // Resolve the dialog this trigger controls.
  //
  // Note: the trigger's `target` attribute points at a <template>, not the live
  // modal — modal-popup clones its contents out at runtime and the resulting
  // element carries no id. So resolving `target` yields a <template>, whose
  // children are in `.content` and invisible to querySelector. Anchor on the
  // navigation drawer itself instead, which only exists inside this menu's
  // dialog and so cannot collide with the cart drawer or newsletter popup.
  function getMenuDialog() {
    var drawer = document.querySelector('.\\#main-navigation-mobile-drawer');
    if (drawer && drawer.closest) {
      var d = drawer.closest('dialog');
      if (d) return d;
    }
    return null;
  }

  function isMenuOpen() {
    var d = getMenuDialog();
    return !!(d && d.open);
  }

  // Close via the theme's own modal-popup API so its teardown runs — it clears
  // the body scroll lock and the opening/closing attributes. Calling
  // dialog.close() raw skips that and leaves the page unscrollable.
  function closeMenu() {
    var d = getMenuDialog();
    if (!d) return;
    var mp = d.closest('modal-popup');
    if (mp && typeof mp.close === 'function') mp.close();
    else if (d.open) d.close();
  }

  /* ---- Close hotspot ------------------------------------------------
     showModal() puts the drawer in the top layer and makes the rest of the
     document inert, so the hamburger — which lives in the header, OUTSIDE the
     dialog — cannot receive real taps while the menu is open. Verified:
     elementFromPoint at the hamburger's centre returns <html>, not the trigger.
     z-index cannot fix this: the "z-index 10005, higher than dialog backdrop"
     rule on the trigger is ineffective, because the top layer sits above every
     stacking context. A synthetic .click() bypasses hit-testing, which is why
     this looked fine under automation but failed on a real tap.

     Fix: put the tap target INSIDE the dialog. The dialog sets no transform,
     filter or contain, so a position:fixed child resolves against the viewport
     and is not clipped by the dialog's overflow:hidden — letting us park an
     invisible button exactly over the rendered X. */
  function syncCloseHotspot() {
    var dialog = getMenuDialog();
    if (!dialog) return;
    var spot = dialog.querySelector('[data-menu-close-hotspot]');

    if (!dialog.open) {
      if (spot) spot.style.display = 'none';
      return;
    }

    if (!spot) {
      spot = document.createElement('button');
      spot.type = 'button';
      spot.setAttribute('data-menu-close-hotspot', '');
      spot.setAttribute('aria-label', 'Close menu');
      spot.style.cssText =
        'position:fixed;margin:0;padding:0;border:0;background:transparent;cursor:pointer;';
      spot.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        closeMenu();
      });
      dialog.appendChild(spot);
    }

    var r = menuTrigger.getBoundingClientRect();
    var pad = 8; // keep a comfortable tap target around the 28px icon
    spot.style.display = 'block';
    spot.style.left = (r.left - pad) + 'px';
    spot.style.top = (r.top - pad) + 'px';
    spot.style.width = (r.width + pad * 2) + 'px';
    spot.style.height = (r.height + pad * 2) + 'px';
  }

  function updateIcon() {
    iconContainer.innerHTML = isMenuOpen() ? closeIcon : hamburgerIcon;
    syncCloseHotspot();
  }

  window.addEventListener('resize', syncCloseHotspot);
  window.addEventListener('orientationchange', syncCloseHotspot);

  // Single capture-phase handler. Only intercepts when THIS menu is genuinely
  // open; otherwise it stays out of the way so modal-trigger opens normally.
  menuTrigger.addEventListener('click', function (e) {
    if (!isMenuOpen()) return; // let modal-trigger do its thing
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    closeMenu();
  }, true);

  // Drive the icon off the dialog's own open/close rather than a body class.
  // modal-popup clones its dialog out of a <template> lazily, so the dialog may
  // not exist until the first open — bind idempotently and also re-try after
  // each click rather than assuming it is present at DOMContentLoaded.
  var observedDialog = null;

  function observeDialog() {
    var d = getMenuDialog();
    if (!d || d === observedDialog) return !!observedDialog;
    observedDialog = d;
    d.addEventListener('close', updateIcon);
    new MutationObserver(updateIcon).observe(d, { attributes: true, attributeFilter: ['open'] });
    updateIcon();
    return true;
  }

  observeDialog();

  menuTrigger.addEventListener('click', function () {
    // Runs on the bubble phase, after modal-trigger has opened the dialog.
    setTimeout(function () { observeDialog(); updateIcon(); }, 60);
  });
});
